import React, { useEffect, useRef } from "react";

// Sci-fi constellation backboard: drifting nodes, hairline links between
// neighbours, and an occasional bright "data pulse" running along a link.
// Two registers:
//   "app" (default) sits fixed behind the whole workspace at z-index -1;
//     body paints --xv-bg, white cards cover it, so it lives in the gutters.
//   "ink" fills the login brand panel, brighter, over the aurora.
// Honors prefers-reduced-motion (single static frame), pauses on hidden tabs,
// caps devicePixelRatio and frame rate so it stays free on old laptops.

const PALETTES = {
  app: {
    node: ["219,31,127", "91,63,184", "21,101,168"], // magenta, lavender, sky inks
    nodeAlpha: 0.45,
    linkAlpha: 0.22,
    pulseColor: "219,31,127",
    pulseAlpha: 0.6,
    linkDist: 150,
  },
  brand: {
    // login panel: light aurora behind, so draw in the brand inks
    node: ["219,31,127", "91,63,184", "242,110,33"],
    nodeAlpha: 0.5,
    linkAlpha: 0.3,
    pulseColor: "242,110,33", // brand gradient orange
    pulseAlpha: 0.8,
    linkDist: 170,
  },
};

function TechBackdrop({ variant = "app" }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    const pal = PALETTES[variant] || PALETTES.app;
    const reduceMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let width = 0;
    let height = 0;
    let nodes = [];
    let pulses = [];
    let raf = 0;
    let lastFrame = 0;
    let running = true;

    const sizeFor = () => {
      const host = variant === "app" ? document.documentElement : canvas.parentElement;
      return {
        w: variant === "app" ? window.innerWidth : host.clientWidth,
        h: variant === "app" ? window.innerHeight : host.clientHeight,
      };
    };

    const build = () => {
      const { w, h } = sizeFor();
      width = w;
      height = h;
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const count = Math.max(24, Math.min(70, Math.round((w * h) / 28000)));
      nodes = Array.from({ length: count }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.22,
        vy: (Math.random() - 0.5) * 0.22,
        r: 1 + Math.random() * 1.4,
        hue: pal.node[Math.floor(Math.random() * pal.node.length)],
      }));
      pulses = [];
    };

    const spawnPulse = () => {
      // pick a random pair that is currently linked and run a spark along it
      for (let tries = 0; tries < 12; tries += 1) {
        const a = nodes[Math.floor(Math.random() * nodes.length)];
        const b = nodes[Math.floor(Math.random() * nodes.length)];
        if (a === b) continue;
        const dx = a.x - b.x;
        const dy = a.y - b.y;
        if (dx * dx + dy * dy < pal.linkDist * pal.linkDist * 0.7) {
          pulses.push({ a, b, t: 0, speed: 0.008 + Math.random() * 0.008 });
          return;
        }
      }
    };

    const draw = (animate) => {
      ctx.clearRect(0, 0, width, height);
      const maxD2 = pal.linkDist * pal.linkDist;

      for (let i = 0; i < nodes.length; i += 1) {
        const n = nodes[i];
        if (animate) {
          n.x += n.vx;
          n.y += n.vy;
          if (n.x < -20) n.x = width + 20;
          if (n.x > width + 20) n.x = -20;
          if (n.y < -20) n.y = height + 20;
          if (n.y > height + 20) n.y = -20;
        }
        for (let j = i + 1; j < nodes.length; j += 1) {
          const m = nodes[j];
          const dx = n.x - m.x;
          const dy = n.y - m.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < maxD2) {
            const strength = 1 - d2 / maxD2;
            ctx.strokeStyle = `rgba(${n.hue},${(pal.linkAlpha * strength).toFixed(3)})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(n.x, n.y);
            ctx.lineTo(m.x, m.y);
            ctx.stroke();
          }
        }
        ctx.fillStyle = `rgba(${n.hue},${pal.nodeAlpha})`;
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fill();
      }

      if (animate) {
        for (let p = pulses.length - 1; p >= 0; p -= 1) {
          const s = pulses[p];
          s.t += s.speed;
          if (s.t >= 1) {
            pulses.splice(p, 1);
            continue;
          }
          const x = s.a.x + (s.b.x - s.a.x) * s.t;
          const y = s.a.y + (s.b.y - s.a.y) * s.t;
          const fade = Math.sin(s.t * Math.PI); // ease in and out of existence
          ctx.fillStyle = `rgba(${pal.pulseColor},${(pal.pulseAlpha * fade).toFixed(3)})`;
          ctx.beginPath();
          ctx.arc(x, y, 2.2, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = `rgba(${pal.pulseColor},${(pal.pulseAlpha * fade * 0.25).toFixed(3)})`;
          ctx.beginPath();
          ctx.arc(x, y, 5.5, 0, Math.PI * 2);
          ctx.fill();
        }
        if (pulses.length < 3 && Math.random() < 0.012) spawnPulse();
      }
    };

    const loop = (ts) => {
      if (!running) return;
      raf = requestAnimationFrame(loop);
      if (ts - lastFrame < 33) return; // ~30fps is plenty for a backdrop
      lastFrame = ts;
      draw(true);
    };

    const onVisibility = () => {
      if (document.hidden) {
        running = false;
        cancelAnimationFrame(raf);
      } else if (!reduceMotion && !running) {
        running = true;
        raf = requestAnimationFrame(loop);
      }
    };

    const onResize = () => {
      build();
      if (reduceMotion) draw(false);
    };

    build();
    if (reduceMotion) {
      draw(false); // one calm frame, no loop
    } else {
      raf = requestAnimationFrame(loop);
      document.addEventListener("visibilitychange", onVisibility);
    }
    window.addEventListener("resize", onResize);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [variant]);

  const style =
    variant === "app"
      ? { position: "fixed", inset: 0, zIndex: -1, pointerEvents: "none" }
      : { position: "absolute", inset: 0, pointerEvents: "none" };

  return <canvas ref={canvasRef} style={style} aria-hidden="true" />;
}

export default TechBackdrop;
